import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import qs from 'qs';
import envConfig from '../../../envConfig';
import { mapSingleAlertV2 } from '../../../classes/mappers/JsonMappers';
import { CrudType } from '../../../utility/types/CrudType';
import { CrudStatus } from '../../../utility/types/CrudStatus';
import RaaAlertMapper from '../../../classes/mappers/RaaAlertMapper';
import RaaAuthenticatedRemoteService from '../../../utility/RaaAuthenticatedRemoteService';
import { selectSessionInformation } from '../sessionSlice';

const selectSingleAlertInitialLoading = (state) => state.singleAlert.initialLoad;

const selectSingleAlert = createSelector([
  (state) => state.singleAlert,
], (singleAlert) => mapSingleAlertV2(singleAlert));

const fetchSingleAlert = createAsyncThunk('securedApi/singleAlert/fetch', async ({ alertId }, thunkAPI) => {
  try {
    return await RaaAuthenticatedRemoteService.globalInstance()
      .get(`${envConfig.apiUrl}/api/v1/alerts/data/${alertId}`);
  } catch (error) {
    return thunkAPI.rejectWithValue(
      error.data
        ? error.data
        : {
          name: 'generic',
          message: error.message,
          data: 'generic',
        },
    );
  }
});

const deleteSingleAlert = createAsyncThunk('securedApi/singleAlert/delete', async ({ alertId }, thunkAPI) => {
  const params = new URLSearchParams();
  params.append('id', alertId);
  try {
    await axios.post(`${envConfig.raaUrl}/alerts/data/delete-alert`, params);
    return thunkAPI.fulfillWithValue(alertId);
  } catch (error) {
    return thunkAPI.rejectWithValue(
      error.data
        ? error.data
        : {
          name: 'generic',
          message: error.message,
          data: 'generic',
        },
    );
  }
});

const editSingleAlert = createAsyncThunk(
  'securedApi/singleAlert/edit', async (alert, thunkAPI) => {
    const config = {
      headers: {
        'content-type': 'application/x-www-form-urlencoded;charset=utf-8',
      },
    };
    const sessionInformation = selectSessionInformation(thunkAPI.getState());
    const data = qs.stringify(RaaAlertMapper.mapAlertToJson(alert, sessionInformation));
    try {
      const result = await axios.post('/alerts/data/update-alert', data, config);
      if (!result.data.success) {
        return thunkAPI.rejectWithValue(result.data.data);
      }
      return thunkAPI.fulfillWithValue(alert);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.data
          ? error.data
          : {
            name: 'generic',
            message: error.message,
            data: 'generic',
          },
      );
    }
  },
);

const selectSingleAlertCrudOperation = createSelector(
  (state) => state.singleAlert,
  (slice) => slice.crudOperation,
);

const selectSingleAlertCrudStatus = createSelector(
  (state) => state.singleAlert,
  (slice) => slice.crudStatus,
);

const selectSingleAlertErrors = createSelector(
  (state) => state.singleAlert,
  (slice) => slice.errors,
);

const initialState = {
  initialLoad: true,
  alert: null,
  crudOperation: CrudType.READ,
  crudStatus: CrudStatus.IDLE,
  errors: [],
};

const singleAlertSlice = createSlice({
  name: 'singleAlert',
  initialState,
  reducers: {
    clearSingleAlertSlice: () => initialState,
    deleteSingleAlertState: (state, action) => {
      if (parseInt(action.payload.id, 10) === parseInt(state.alert.id, 10)) {
        state.alert = null;
        state.crudOperation = CrudType.DELETE;
        state.crudStatus = CrudStatus.SUCCESS;
      }
    },
    setSingleAlertIsFavorite: (state, action) => {
      const { isFavorite } = action.payload;
      state.isFavorite = isFavorite;
    },
    acknowledgeSingleAlert: (state, action) => {
      const { alertEvent } = action.payload;
      const isAcknowledged = !!alertEvent.isAcknowledged;

      state.alertEvent = {
        ...state.alertEvent,
        ...alertEvent,
        acknowledgedAt: isAcknowledged ? alertEvent.acknowledgedAt : null,
      };
    },
    updateSingleAlertDescription: (state, action) => {
      const { description } = action.payload;
      state.alert.description = description;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSingleAlert.pending, (state) => {
        state.crudStatus = CrudStatus.PENDING;
        state.crudOperation = CrudType.READ;
      })
      .addCase(fetchSingleAlert.fulfilled, (state, action) => {
        Object.assign(state, action.payload); // Merges payload properties into state
        state.crudStatus = CrudStatus.SUCCESS;
        state.initialLoad = false;
      })
      .addCase(fetchSingleAlert.rejected, (state, action) => {
        state.crudStatus = CrudStatus.ERROR;
        state.errors = action.payload;
      })
      .addCase(deleteSingleAlert.pending, (state) => {
        state.errors = [];
        state.crudOperation = CrudType.DELETE;
        state.crudStatus = CrudStatus.PENDING;
      })
      .addCase(deleteSingleAlert.fulfilled, (state) => {
        state.crudStatus = CrudStatus.SUCCESS;
        state.alert = null;
      })
      .addCase(deleteSingleAlert.rejected, (state, action) => {
        state.crudStatus = CrudStatus.ERROR;
        state.errors = action.payload;
      })
      .addCase(editSingleAlert.pending, (state) => {
        state.errors = [];
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.PENDING;
      })
      .addCase(editSingleAlert.fulfilled, (state) => {
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.SUCCESS;
      })
      .addCase(editSingleAlert.rejected, (state, action) => {
        state.initialLoad = false;
        state.crudStatus = CrudStatus.ERROR;
        state.errors = action.payload;
      });
  },
});

const { actions, reducer } = singleAlertSlice;

export const {
  clearSingleAlertSlice,
  deleteSingleAlertState,
  setSingleAlertIsFavorite,
  acknowledgeSingleAlert,
  updateSingleAlertDescription,
} = actions;
export {
  fetchSingleAlert,
  selectSingleAlert,
  selectSingleAlertCrudOperation,
  selectSingleAlertCrudStatus,
  deleteSingleAlert,
  selectSingleAlertErrors,
  editSingleAlert,
  selectSingleAlertInitialLoading,
};
export default reducer;
